import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

const UNIT_CLASS = 'word-rise__unit';
const INNER_CLASS = 'word-rise__inner';
const SKIP_TAGS = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'CODE', 'PRE', 'TEXTAREA', 'SVG']);
const EASING_FALLBACK = 'cubic-bezier(0.16, 1, 0.3, 1)';

/**
 * Reveals heading copy one word at a time as the block enters the viewport.
 *
 * @extends {Component}
 */
class WordRise extends Component {
  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {Animation[]} */
  #animations = [];

  /** @type {boolean} */
  #played = false;

  connectedCallback() {
    super.connectedCallback();

    if (prefersReducedMotion()) {
      this.dataset.prepared = 'true';
      this.dataset.shown = 'true';
      return;
    }

    requestAnimationFrame(() => {
      if (!this.isConnected) return;
      this.#prepare();
      this.#watch();
    });
  }

  updatedCallback() {
    super.updatedCallback();
    this.#reset();

    if (prefersReducedMotion()) {
      this.dataset.prepared = 'true';
      this.dataset.shown = 'true';
      return;
    }

    this.#prepare();
    this.#watch();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#teardown();
  }

  #prepare() {
    if (this.querySelector(`.${UNIT_CLASS}`)) {
      this.dataset.prepared = 'true';
      return;
    }

    const nodes = [];
    const walker = document.createTreeWalker(this, NodeFilter.SHOW_TEXT, {
      acceptNode: (node) => {
        const parent = node.parentElement;
        if (!parent || SKIP_TAGS.has(parent.tagName)) return NodeFilter.FILTER_REJECT;
        if (parent.closest(`.${UNIT_CLASS}`)) return NodeFilter.FILTER_REJECT;
        if (!node.nodeValue || !node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
        return NodeFilter.FILTER_ACCEPT;
      },
    });

    while (walker.nextNode()) {
      nodes.push(walker.currentNode);
    }

    for (const node of nodes) {
      this.#wrapTextNode(node);
    }

    this.dataset.prepared = 'true';
  }

  /**
   * @param {Text} node
   */
  #wrapTextNode(node) {
    const text = node.nodeValue;
    if (!text) return;

    const fragment = document.createDocumentFragment();

    for (const part of text.split(/(\s+)/)) {
      if (!part) continue;

      if (/^\s+$/.test(part)) {
        fragment.appendChild(document.createTextNode(part));
        continue;
      }

      const unit = document.createElement('span');
      unit.className = UNIT_CLASS;

      const inner = document.createElement('span');
      inner.className = INNER_CLASS;
      inner.textContent = part;
      unit.appendChild(inner);
      fragment.appendChild(unit);
    }

    node.replaceWith(fragment);
  }

  #watch() {
    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[entries.length - 1];
        if (!entry?.isIntersecting || this.#played) return;

        this.#observer?.disconnect();
        this.#play();
      },
      {
        threshold: 0.18,
        rootMargin: '0px 0px -8% 0px',
      }
    );

    this.#observer.observe(this);
  }

  async #play() {
    if (this.#played) return;
    this.#played = true;

    const inners = [...this.querySelectorAll(`.${INNER_CLASS}`)];
    if (inners.length === 0) {
      this.dataset.shown = 'true';
      return;
    }

    const stagger = Number(this.dataset.stagger || 28);
    const duration = Number(this.dataset.duration || 900);
    const delay = Number(this.dataset.delay || 60);
    const easing =
      getComputedStyle(this).getPropertyValue('--animation-timing-fade-in').trim() || EASING_FALLBACK;

    this.#animations = inners.map((inner, index) =>
      inner.animate(
        [
          { transform: 'translate3d(0, 1.15em, 0)', opacity: 0 },
          { transform: 'translate3d(0, 0, 0)', opacity: 1 },
        ],
        {
          duration,
          delay: delay + index * stagger,
          easing,
          fill: 'forwards',
        }
      )
    );

    await Promise.all(this.#animations.map((animation) => animation.finished.catch(() => {})));
    this.dataset.shown = 'true';
  }

  #reset() {
    this.#teardown();
    this.#played = false;
    delete this.dataset.prepared;
    delete this.dataset.shown;
  }

  #teardown() {
    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#animations.forEach((animation) => animation.cancel());
    this.#animations = [];
  }
}

if (!customElements.get('word-rise')) {
  customElements.define('word-rise', WordRise);
}
